import { useCallback, useEffect, useRef } from 'react';
import { CHAT_MESSAGE_CHUNK_SIZE, fileToDataUrl, MEET_CHAT_IMAGE_TOPIC, publishChunkedChatMessage, publishReliableChatData, } from './chatDataChannelUtils';
export function useLiveKitChat({ room, onChatMessage }) {
    const chatChunksRef = useRef(new Map());
    const pendingChatImagesRef = useRef(new Map());
    const onChatMessageRef = useRef(onChatMessage);
    onChatMessageRef.current = onChatMessage;
    const sendChatMessage = useCallback(async (message) => {
        if (!room) {
            return false;
        }
        const imageFile = message.kind === 'image' ? message.image?.file : undefined;
        if (imageFile && message.image) {
            const metaPayload = {
                type: 'chat_message',
                id: message.id,
                kind: 'image',
                text: message.text,
                sentAt: message.sentAt,
                senderName: message.senderName,
                from: room.localParticipant.identity,
                imageStreamId: message.id,
                mimeType: message.image.mimeType,
                width: message.image.width,
                height: message.image.height,
            };
            try {
                if (typeof room.localParticipant.streamBytes === 'function') {
                    await publishReliableChatData(room, JSON.stringify(metaPayload));
                    const writer = await room.localParticipant.streamBytes({
                        topic: MEET_CHAT_IMAGE_TOPIC,
                        mimeType: message.image.mimeType,
                        streamId: message.id,
                        totalSize: imageFile.size,
                        name: 'chat-image.jpg',
                    });
                    await writer.write(new Uint8Array(await imageFile.arrayBuffer()));
                    await writer.close();
                    return true;
                }
                if (typeof room.localParticipant.sendFile === 'function') {
                    const streamPromise = room.localParticipant.sendFile(imageFile, {
                        topic: MEET_CHAT_IMAGE_TOPIC,
                        mimeType: message.image.mimeType,
                    });
                    const { id: streamId } = await streamPromise;
                    await publishReliableChatData(room, JSON.stringify({
                        ...metaPayload,
                        imageStreamId: streamId,
                    }));
                    return true;
                }
            }
            catch (error) {
                console.warn('Byte stream image send failed, falling back to data channel', error);
            }
        }
        let inlineImage = message.image;
        if (message.kind === 'image' && message.image?.file && !message.image.dataUrl.startsWith('data:')) {
            inlineImage = {
                ...message.image,
                dataUrl: await fileToDataUrl(message.image.file),
            };
        }
        const nextMessage = {
            type: 'chat_message',
            id: message.id,
            kind: message.kind,
            text: message.text,
            sentAt: message.sentAt,
            senderName: message.senderName,
            from: room.localParticipant.identity,
            image: inlineImage
                ? {
                    dataUrl: inlineImage.dataUrl,
                    mimeType: inlineImage.mimeType,
                    width: inlineImage.width,
                    height: inlineImage.height,
                }
                : undefined,
        };
        const serializedMessage = JSON.stringify(nextMessage);
        try {
            if (serializedMessage.length <= CHAT_MESSAGE_CHUNK_SIZE) {
                await publishReliableChatData(room, serializedMessage);
                return true;
            }
            await publishChunkedChatMessage(room, message.id, serializedMessage);
            return true;
        }
        catch (error) {
            console.error('Failed to send chat message', error);
            return false;
        }
    }, [room]);
    const tryCompletePendingChatImage = useCallback((streamId) => {
        const pending = pendingChatImagesRef.current.get(streamId);
        if (!pending?.meta || !pending.blob || !pending.senderId) {
            return;
        }
        onChatMessageRef.current?.({
            id: pending.meta.id,
            kind: 'image',
            text: pending.meta.text,
            sentAt: pending.meta.sentAt,
            senderId: pending.senderId,
            senderName: pending.meta.senderName,
            image: {
                dataUrl: URL.createObjectURL(pending.blob),
                mimeType: pending.meta.mimeType || pending.blob.type || 'image/jpeg',
                width: pending.meta.width,
                height: pending.meta.height,
            },
        });
        pendingChatImagesRef.current.delete(streamId);
    }, []);
    useEffect(() => {
        if (!room || typeof room.registerByteStreamHandler !== 'function') {
            return;
        }
        const handleChatImageStream = async (reader, participantInfo) => {
            try {
                const streamId = reader.info.id;
                const chunks = await reader.readAll();
                const blob = new Blob(chunks, { type: reader.info.mimeType || 'image/jpeg' });
                const existing = pendingChatImagesRef.current.get(streamId) ?? {};
                pendingChatImagesRef.current.set(streamId, {
                    ...existing,
                    blob,
                    senderId: participantInfo.identity,
                });
                tryCompletePendingChatImage(streamId);
            }
            catch (error) {
                console.error('Failed to receive chat image stream', error);
            }
        };
        room.registerByteStreamHandler(MEET_CHAT_IMAGE_TOPIC, handleChatImageStream);
        return () => {
            room.unregisterByteStreamHandler(MEET_CHAT_IMAGE_TOPIC);
        };
    }, [room, tryCompletePendingChatImage]);
    const handleChatData = useCallback((parsedMessage, participant) => {
        let parsed = parsedMessage;
        if (parsed.type === 'chat_message_chunk') {
            const transferId = typeof parsed.transferId === 'string' ? parsed.transferId : '';
            const chunkIndex = typeof parsed.chunkIndex === 'number' ? parsed.chunkIndex : -1;
            const totalChunks = typeof parsed.totalChunks === 'number' ? parsed.totalChunks : 0;
            const chunk = typeof parsed.chunk === 'string' ? parsed.chunk : '';
            if (!transferId || chunkIndex < 0 || totalChunks <= 0 || !chunk) {
                return { parsed, stopPropagation: true };
            }
            const existingTransfer = chatChunksRef.current.get(transferId) ?? {
                totalChunks,
                chunks: new Array(totalChunks).fill(''),
            };
            existingTransfer.chunks[chunkIndex] = chunk;
            chatChunksRef.current.set(transferId, existingTransfer);
            if (existingTransfer.chunks.some((item) => item === '')) {
                return { parsed, stopPropagation: true };
            }
            chatChunksRef.current.delete(transferId);
            parsed = JSON.parse(existingTransfer.chunks.join(''));
        }
        if (parsed.type === 'chat_message') {
            if (room.localParticipant.identity !== parsed.from) {
                const id = typeof parsed.id === 'string' ? parsed.id : '';
                const text = typeof parsed.text === 'string' ? parsed.text : '';
                const sentAt = typeof parsed.sentAt === 'string' ? parsed.sentAt : new Date().toISOString();
                const kind = parsed.kind === 'image' ? 'image' : 'text';
                const imageStreamId = typeof parsed.imageStreamId === 'string' ? parsed.imageStreamId : '';
                const senderId = String(parsed.from ?? participant?.identity ?? '');
                if (kind === 'image' && imageStreamId && id) {
                    const mimeType = typeof parsed.mimeType === 'string' ? parsed.mimeType : 'image/jpeg';
                    const width = typeof parsed.width === 'number' ? parsed.width : 0;
                    const height = typeof parsed.height === 'number' ? parsed.height : 0;
                    const existing = pendingChatImagesRef.current.get(imageStreamId) ?? {};
                    pendingChatImagesRef.current.set(imageStreamId, {
                        ...existing,
                        senderId,
                        meta: {
                            id,
                            text,
                            sentAt,
                            senderName: typeof parsed.senderName === 'string' ? parsed.senderName : undefined,
                            mimeType,
                            width,
                            height,
                        },
                    });
                    tryCompletePendingChatImage(imageStreamId);
                    return { parsed, stopPropagation: true };
                }
                const image = parsed.image &&
                    typeof parsed.image.dataUrl === 'string' &&
                    typeof parsed.image.mimeType === 'string' &&
                    typeof parsed.image.width === 'number' &&
                    typeof parsed.image.height === 'number'
                    ? {
                        dataUrl: parsed.image.dataUrl,
                        mimeType: parsed.image.mimeType,
                        width: parsed.image.width,
                        height: parsed.image.height,
                    }
                    : undefined;
                if (id && (text.trim() || image)) {
                    onChatMessageRef.current?.({
                        id,
                        kind,
                        text,
                        sentAt,
                        senderId,
                        senderName: typeof parsed.senderName === 'string' ? parsed.senderName : undefined,
                        image,
                    });
                }
            }
        }
        return { parsed, stopPropagation: false };
    }, [room, tryCompletePendingChatImage]);
    return {
        handleChatData,
        sendChatMessage,
    };
}
